/* eslint-disable import/order */
import React, {
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
} from 'react';
import PropTypes from 'prop-types';
import { useStripes } from '@folio/stripes/core';
import { FormattedMessage } from 'react-intl';
import { flatMap, isEmpty, chunk } from 'lodash';
import useTenantKy from '../../../../hooks/useTenantKy';
import {
  setHoldingPostPayload,
  setInstancePayload,
  setItemPostPayload,
} from '../../../../utils/utils';
import { BarcodeLastIndexContext } from '../../../../contexts';
import CreateSubmitModal from './CreateSubmitModal';
import { v4 as uuidv4 } from 'uuid';

// Batch processing configuration - same as EditInstance
const BATCH_CONFIG = {
  SMALL_DATASET: { batchSize: 15, delay: 200 },
  MEDIUM_DATASET: { batchSize: 12, delay: 300 },
  LARGE_DATASET: { batchSize: 8, delay: 400 },
};

// Delay between requests
const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// Message component - same as EditInstance
function createMessage(text, type) {
  return React.createElement(
    'div',
    {
      style: {
        padding: '8px 12px',
        borderRadius: '4px',
        margin: '4px 0',
        backgroundColor:
          type === 'success'
            ? '#d4edda'
            : type === 'error'
              ? '#f8d7da'
              : '#d1ecf1',
        border: `1px solid ${type === 'success' ? '#c3e6cb' : type === 'error' ? '#f5c6cb' : '#bee5eb'}`,
        color:
          type === 'success'
            ? '#155724'
            : type === 'error'
              ? '#721c24'
              : '#0c5460',
      },
    },
    text,
  );
}

function CreateProgressModal({ onClose, open, formValues, history, context }) {
  const {
    okapi: { tenant },
  } = useStripes();
  const ky = useTenantKy({ tenantId: tenant });
  const { configId, barcodeLastIndex } = useContext(BarcodeLastIndexContext);

  // Message and progress states
  const [instanceProgressMsg, setInstanceProgressMsg] = useState([]);
  const [holdingsProgressMsg, setHoldingsProgressMsg] = useState([]);
  const [itemsProgressMsg, setItemsProgressMsg] = useState([]);
  const [errorMessages, setErrorMessages] = useState([]);

  // Operation states
  const [totalProcesses, setTotalProcesses] = useState(0);
  const [processesSummary, setProcessesSummary] = useState([]);
  const [processCounter, setProcessCounter] = useState(0);
  const [startUpdate, setStartUpdate] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [currentBatch, setCurrentBatch] = useState(0);
  const [showErrors, setShowErrors] = useState(false);
  const [createdInstanceId, setCreatedInstanceId] = useState(null);

  const barcodeUpdateCountRef = useRef(0);

  // Extract detailed error message from API response
  const extractErrorMessage = async (error) => {
    let errorText = error.message; // Default message

    if (error.response) {
      try {
        const contentType = error.response.headers.get('content-type');

        if (contentType && contentType.includes('application/json')) {
          // Clone the response to avoid consuming the stream
          const responseClone = error.response.clone();
          const errorData = await responseClone.json();

          // FOLIO API usually sends errors in one of these formats:
          errorText =
            errorData.errors?.[0]?.message ||
            errorData.error ||
            errorData.message ||
            errorData.errorMessage ||
            errorData.msg ||
            errorData.detail ||
            errorData.title ||
            JSON.stringify(errorData);

          // If still generic, try to get more specific info
          if (errorText === 'Bad Request' && errorData.errors?.[0]) {
            const firstError = errorData.errors[0];
            errorText = firstError.code
              ? `${firstError.code}: ${firstError.message || firstError.parameters || 'Bad Request'}`
              : firstError.message || firstError.parameters || 'Bad Request';
          }
        } else {
          // If response is plain text
          const responseClone = error.response.clone();
          errorText = await responseClone.text();
        }

        // Enhanced error code handling
        if (
          error.response.status &&
          (errorText === 'Bad Request' || errorText.trim() === '')
        ) {
          errorText = `HTTP ${error.response.status}: ${error.response.statusText || 'Bad Request'} - Check the data format`;
        }
      } catch (e) {
        // If extraction fails, provide more detailed fallback
        console.warn('Failed to extract error message:', e);
        errorText = `HTTP ${error.response.status || 'Unknown'}: ${error.response.statusText || error.message}`;
      }
    }

    return errorText;
  };

  // Enhanced error handling - same as EditInstance
  const handleApiError = useCallback(
    async (error, operation, category, displayMessage) => {
      let errorMessage = error.message;
      let errorCode = 'UNKNOWN';

      // Extract detailed error text like in DefaultFields
      if (error.response) {
        try {
          const text = await error.response.text();
          errorMessage = text || error.message;
          errorCode = error.response.status || 'UNKNOWN';
        } catch (e) {
          errorMessage = error.message;
        }
      }

      // Enhanced error object with more details
      const enhancedError = {
        category,
        operation,
        message: errorMessage,
        displayMessage:
          displayMessage || `Failed to ${operation}: ${errorMessage}`,
        timestamp: new Date().toISOString(),
        code: errorCode,
      };

      setErrorMessages((prev) => [...prev, enhancedError]);
    },
    [],
  );

  // Helper functions - same as EditInstance
  const addInstanceMessage = useCallback((text, type = 'info') => {
    setInstanceProgressMsg((prev) => [...prev, createMessage(text, type)]);
  }, []);

  const addHoldingsMessage = useCallback((text, type = 'info') => {
    setHoldingsProgressMsg((prev) => [...prev, createMessage(text, type)]);
  }, []);

  const addItemsMessage = useCallback((text, type = 'info') => {
    setItemsProgressMsg((prev) => [...prev, createMessage(text, type)]);
  }, []);

  const incrementCounter = useCallback(() => {
    setProcessCounter((prev) => prev + 1);
  }, []);

  // Handle modal closure like EditInstance
  const handleCloseModal = useCallback(() => {
    const instanceTitle = formValues?.instanceData?.title || 'Instance';

    // Send success callout
    if (context && context.sendCallout) {
      context.sendCallout({
        message: (
          <FormattedMessage
            id="ui-plugin-inventory-templates.createProgress.callout.success"
            defaultMessage='Instance "{title}" created successfully'
            values={{ title: instanceTitle }}
          />
        ),
        type: 'success',
      });
    }

    // Navigate to created instance
    if (createdInstanceId && history) {
      history.push(`/inventory/view/${createdInstanceId}`);
    }

    // Close modal
    onClose();
  }, [formValues, createdInstanceId, history, context, onClose]);

  // Determine optimal configuration based on data size
  const getOptimalConfig = useCallback((operationsCount) => {
    if (operationsCount <= 50) return BATCH_CONFIG.SMALL_DATASET;
    if (operationsCount <= 200) return BATCH_CONFIG.MEDIUM_DATASET;
    return BATCH_CONFIG.LARGE_DATASET;
  }, []);

  // Process operations in batches
  const processBatch = useCallback(async (operations, config, processor) => {
    const batches = chunk(operations, config.batchSize);
    const results = { successful: 0, failed: 0 };

    for (let i = 0; i < batches.length; i++) {
      setCurrentBatch(i + 1);
      const batch = batches[i];

      // Parallel processing with concurrency control
      const concurrencyLimit = Math.min(3, batch.length);
      const chunks = chunk(batch, concurrencyLimit);

      for (const subChunk of chunks) {
        // Use allSettled to continue even if some operations fail
        const settledResults = await Promise.allSettled(
          subChunk.map((operation) => processor(operation)),
        );

        // Count successes and failures
        settledResults.forEach((result) => {
          if (
            result.status === 'fulfilled' &&
            result.value?.success !== false
          ) {
            results.successful++;
          } else {
            results.failed++;
            if (result.status === 'rejected') {
              console.error('Operation failed:', result.reason);
            }
          }
        });
      }

      // Reduced delay between batches
      if (i < batches.length - 1) {
        await delay(config.delay);
      }
    }

    return results;
  }, []);

  // Update barcode configuration - same as EditInstance
  const updateBarcodeConfig = useCallback(async () => {
    if (barcodeUpdateCountRef.current === 0) return;

    try {
      await ky.put(`inventory-templates-configs/${configId}`, {
        json: {
          configName: 'barcodeLastIndex',
          configCode: 'barcodeLastIndex',
          configValue: {
            lastIndex: barcodeLastIndex + barcodeUpdateCountRef.current,
          },
        },
      });

      addItemsMessage(
        <FormattedMessage
          id="ui-plugin-inventory-templates.createProgress.barcode.updateSuccess"
          defaultMessage="Barcode configuration updated successfully. New last index: {index}"
          values={{ index: barcodeLastIndex + barcodeUpdateCountRef.current }}
        />,
        'success',
      );
      barcodeUpdateCountRef.current = 0;
    } catch (error) {
      // Silently handle barcode update errors
      addItemsMessage(
        <FormattedMessage
          id="ui-plugin-inventory-templates.createProgress.barcode.updateError"
          defaultMessage="Failed to update barcode configuration"
        />,
        'error',
      );
    }
  }, [ky, configId, barcodeLastIndex, addItemsMessage]);

  // Process Items - adapted for create operations only
  const processItems = useCallback(
    async (itemsToProcess, config) => {
      if (!itemsToProcess || itemsToProcess.length === 0) return;

      const processor = async (item) => {
        try {
          // Only create operation for new instances
          const result = await ky
            .post('inventory/items', {
              json: setItemPostPayload(item),
            })
            .json();
          barcodeUpdateCountRef.current++;
          addItemsMessage(
            <FormattedMessage
              id="ui-plugin-inventory-templates.createProgress.items.success"
              defaultMessage="Created item: {barcode}"
              values={{ barcode: item.barcode }}
            />,
            'success',
          );

          incrementCounter();
          return result;
        } catch (error) {
          incrementCounter();
          // Extract detailed error message
          const errorText = await extractErrorMessage(error);
          const errorMsg = (
            <FormattedMessage
              id="ui-plugin-inventory-templates.createProgress.items.error"
              defaultMessage="Failed to create item: {error}"
              values={{ error: errorText }}
            />
          );
          addItemsMessage(errorMsg, 'error');
          await handleApiError(
            error,
            <FormattedMessage
              id="ui-plugin-inventory-templates.createProgress.operation.createItem"
              defaultMessage="Create item {barcode}"
              values={{
                barcode: item.barcode || (
                  <FormattedMessage
                    id="ui-plugin-inventory-templates.createProgress.operation.unknown"
                    defaultMessage="Unknown"
                  />
                ),
              }}
            />,
            'items',
            errorMsg,
          );
          // Don't throw - allow other items to be processed
          return { success: false, error, item };
        }
      };

      await processBatch(itemsToProcess, config, processor);
    },
    [ky, addItemsMessage, incrementCounter, processBatch],
  );

  // Process Holdings - adapted for create operations only
  const processHoldings = useCallback(
    async (holdingsToProcess, config, newInstanceId) => {
      if (!holdingsToProcess || holdingsToProcess.length === 0) return;

      const processor = async (holding) => {
        try {
          // Update formValues with the new instanceId before calling setHoldingPostPayload
          const updatedFormValues = {
            ...formValues,
            instanceData: {
              ...formValues.instanceData,
              id: newInstanceId,
            },
          };

          // Only create operation for new instances
          const holdingPayload = setHoldingPostPayload(
            holding,
            updatedFormValues,
          );

          const result = await ky
            .post('holdings-storage/holdings', {
              json: holdingPayload,
            })
            .json();
          addHoldingsMessage(
            <FormattedMessage
              id="ui-plugin-inventory-templates.createProgress.holdings.success"
              defaultMessage="Created holding: {id}"
              values={{ id: holding.id }}
            />,
            'success',
          );

          // Process associated items
          if (
            formValues.itemsData &&
            formValues.itemsData[holding.id]?.length
          ) {
            // Update items to reference the created holding
            const itemsWithHoldingId = formValues.itemsData[holding.id].map(
              (item) => ({
                ...item,
                holdingsRecordId: result.id,
              }),
            );

            await processItems(itemsWithHoldingId, config);
          }

          incrementCounter();
          return result;
        } catch (error) {
          incrementCounter();
          // Extract detailed error message
          const errorText = await extractErrorMessage(error);
          const errorMsg = (
            <FormattedMessage
              id="ui-plugin-inventory-templates.createProgress.holdings.error"
              defaultMessage="Failed to create holding"
            />
          );
          addHoldingsMessage(errorMsg, 'error');
          await handleApiError(
            error,
            <FormattedMessage
              id="ui-plugin-inventory-templates.createProgress.operation.createHolding"
              defaultMessage="Create holding {location}"
              values={{
                location: holding.permanentLocationId || (
                  <FormattedMessage
                    id="ui-plugin-inventory-templates.createProgress.operation.unknown"
                    defaultMessage="Unknown"
                  />
                ),
              }}
            />,
            'holdings',
            errorMsg,
          );
          // Don't throw - allow other holdings to be processed
          return { success: false, error, holding };
        }
      };

      await processBatch(holdingsToProcess, config, processor);
    },
    [
      ky,
      formValues,
      addHoldingsMessage,
      incrementCounter,
      processBatch,
      processItems,
    ],
  );

  // Main process execution - adapted for create operations
  const executeMainProcess = useCallback(async () => {
    if (!startUpdate || isProcessing) return;

    setIsProcessing(true);
    let instanceCreated = false;
    let newInstanceId = null;

    try {
      // Create Instance first with separate error handling
      addInstanceMessage(
        <FormattedMessage
          id="ui-plugin-inventory-templates.createProgress.instance.starting"
          defaultMessage="Starting instance creation..."
        />,
        'info',
      );

      newInstanceId = uuidv4();
      const instancePayload = setInstancePayload(
        formValues,
        newInstanceId,
        {}, // No existing instance
      );

      try {
        await ky.post('inventory/instances', {
          json: instancePayload,
        });

        // Store the created instance ID for navigation
        setCreatedInstanceId(newInstanceId);
        addInstanceMessage(
          <FormattedMessage
            id="ui-plugin-inventory-templates.createProgress.instance.success"
            defaultMessage="Instance created successfully: {title}"
            values={{ title: formValues.instanceData.title }}
          />,
          'success',
        );
        incrementCounter();
        instanceCreated = true;
      } catch (error) {
        // Handle instance creation error specifically
        const errorText = await extractErrorMessage(error);
        addInstanceMessage(
          <FormattedMessage
            id="ui-plugin-inventory-templates.createProgress.instance.error"
            defaultMessage="Failed to create instance"
          />,
          'error',
        );
        await handleApiError(
          error,
          <FormattedMessage
            id="ui-plugin-inventory-templates.createProgress.operation.createInstance"
            defaultMessage="Create instance"
          />,
          'instance',
          <FormattedMessage
            id="ui-plugin-inventory-templates.createProgress.instance.error"
            defaultMessage="Failed to create instance: {error}"
            values={{ error: errorText }}
          />,
        );
        // Don't continue if instance creation failed
        return;
      }

      // Process Holdings only if instance was created successfully
      if (instanceCreated && formValues.holdingsData?.length > 0) {
        const holdingsToCreate = formValues.holdingsData;
        const config = getOptimalConfig(holdingsToCreate.length);

        addHoldingsMessage(
          <FormattedMessage
            id="ui-plugin-inventory-templates.createProgress.holdings.starting"
            defaultMessage="Starting holdings creation..."
          />,
          'info',
        );
        // processHoldings will handle its own errors now
        await processHoldings(holdingsToCreate, config, newInstanceId);
      }

      // Update barcode configuration at the end
      await updateBarcodeConfig();

      // Show completion message based on whether there were errors
      if (errorMessages.length === 0) {
        addInstanceMessage(
          <FormattedMessage
            id="ui-plugin-inventory-templates.createProgress.completion.success"
            defaultMessage="All operations completed successfully!"
          />,
          'success',
        );
      } else {
        addInstanceMessage(
          <FormattedMessage
            id="ui-plugin-inventory-templates.createProgress.completion.withErrors"
            defaultMessage="Process completed with {count} error(s). Check error details for more information."
            values={{ count: errorMessages.length }}
          />,
          'warning',
        );
      }
    } catch (error) {
      // This should rarely happen now as we handle errors in specific blocks
      console.error('Unexpected error in main process:', error);
      const errorText = await extractErrorMessage(error);
      addInstanceMessage(
        <FormattedMessage
          id="ui-plugin-inventory-templates.createProgress.completion.unexpectedError"
          defaultMessage="Unexpected error occurred"
        />,
        'error',
      );
    } finally {
      setIsProcessing(false);
      setCurrentBatch(0);
    }
  }, [
    startUpdate,
    formValues,
    ky,
    addInstanceMessage,
    addHoldingsMessage,
    incrementCounter,
    getOptimalConfig,
    processHoldings,
    updateBarcodeConfig,
    handleApiError,
    extractErrorMessage,
    errorMessages,
    history,
    onClose,
  ]);

  // Calculate total operations - simplified for create only
  useEffect(() => {
    if (!isEmpty(formValues)) {
      const holdingsToCreate = formValues.holdingsData || [];
      const itemsToCreate = flatMap(formValues?.itemsData) || [];

      setProcessesSummary([
        { category: 'instance', new: 1, updated: 0, deleted: 0 },
        {
          category: 'holdings',
          new: holdingsToCreate.length,
          updated: 0,
          deleted: 0,
        },
        {
          category: 'items',
          new: itemsToCreate.length,
          updated: 0,
          deleted: 0,
        },
      ]);

      const totalOps = 1 + holdingsToCreate.length + itemsToCreate.length;
      setTotalProcesses(totalOps);
    }
  }, [formValues]);

  // Execute process when update starts
  useEffect(() => {
    if (startUpdate && !isProcessing) {
      executeMainProcess();
    }
  }, [startUpdate]);

  return (
    <CreateSubmitModal
      open={open}
      onClose={onClose}
      processCounter={processCounter}
      instanceProgressMsg={instanceProgressMsg}
      holdingsProgressMsg={holdingsProgressMsg}
      itemsProgressMsg={itemsProgressMsg}
      errorMessages={errorMessages}
      totalProcesses={totalProcesses}
      startUpdate={startUpdate}
      setStartUpdate={setStartUpdate}
      processesSummary={processesSummary}
      isProcessing={isProcessing}
      currentBatch={currentBatch}
      showErrors={showErrors}
      setShowErrors={setShowErrors}
      formValues={formValues}
      handleCloseModal={handleCloseModal}
    />
  );
}

CreateProgressModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  formValues: PropTypes.object,
  history: PropTypes.object,
  context: PropTypes.object,
};

CreateProgressModal.defaultProps = {
  open: false,
  formValues: {},
  history: {},
  context: {},
};

export default CreateProgressModal;
